var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Line, LineChart, YAxis, CartesianGrid, XAxis, Legend, ReferenceLine, Tooltip, } from "recharts";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useTheme } from "@emotion/react";
import { graphMargins, graphWidth, defaultXAxisProps, liftingForcesGraphHeight, yAxisDyTSpan, yAxisPadding, liftingForcesYAxisHorizontalKey, liftingForcesYAxisVerticalKey, defaultGraphStrokeWidth, liftingForcesColor, liftingForcesYAxisUnit, liftingForceTooltipPosition, defaultYAxisProps, } from "./constants";
import { VerticalReferenceLines } from "./common/reference-lines";
import { CustomXAxisTick } from "./common/custom-x-axis-tick";
import { useCustomLegendWrapperStyle } from "./common/custom-legend";
import { useTimeDataMinMaxDomain } from "hooks/graph-panel/graph";
import { selectors, useAppSelector } from "store";
import { useNumberConversion } from "hooks/number-conversion";
import { CustomYAxisTick } from "ui/components/panels/graphs/common/custom-y-axis-tick";
import { CustomLiftingForcesGraphTooltip } from "ui/components/panels/graphs/common/custom-lifting-forces-graph-tooltip";
import { LiftingForcesLegend } from "ui/components/panels/graphs/lifting-forces-legend";
import { useGraphDataContext } from "providers/graph-panel";
import { useGraphLabels } from "hooks/graph-panel/graph-labels";
import { UnitSystem } from "store/types";
import { conversions } from "utility/number";
import { boxLiftingForceDataKey, canFitTickForZero, filterDataToDomain, } from "ui/components/panels/graphs/common/helper-functions";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var YAxisTick = function (props) {
    var x = props.x, payload = props.payload;
    var convertNumber = useNumberConversion();
    var convertedValue = convertNumber(payload.value, liftingForcesYAxisUnit);
    var label = (_jsx("tspan", { x: x, dx: 0, dy: yAxisDyTSpan, children: convertedValue.value }));
    return _jsx(CustomYAxisTick, __assign({}, props, { labelElements: label }));
};
export var LiftingForcesGraph = function () {
    var t = useTranslation().t;
    var xAxisDomain = useTimeDataMinMaxDomain();
    var unitSystem = useAppSelector(selectors.selectUnitSystem);
    var graphData = useAppSelector(selectors.selectGraphData);
    var liftingForcesGraphData = useAppSelector(selectors.selectLiftingForcesGraphData);
    var theme = useTheme();
    var showEnergySave = useAppSelector(selectors.selectShowEnergySave);
    var systemProperty = graphData.systemProperty;
    var esIndex = systemProperty.esIndex, cupReleaseIndex = systemProperty.cupReleaseIndex, psIndex = systemProperty.psIndex, timeData = systemProperty.timeData;
    var _a = useGraphDataContext(), pumpsAndCupsNodeTree = _a.pumpsAndCupsNodeTree, selectedNodes = _a.selectedNodes, zoomedLiftForceDomain = _a.zoomedLiftForceDomain, zoomedTimeDomain = _a.zoomedTimeDomain;
    var getSuctionCupLabel = useGraphLabels().getSuctionCupLabel;
    var customLegendWrapperStyle = useCustomLegendWrapperStyle(liftingForcesGraphHeight);
    var selectedNodesCount = useMemo(function () {
        return Object.values(selectedNodes !== null && selectedNodes !== void 0 ? selectedNodes : {}).reduce(function (acc, isSelected) { return (isSelected ? acc + 1 : acc); }, 0);
    }, [selectedNodes]);
    // Piab wants vacuum pump lines to be thinner when multiple nodes are selected
    var strokeWidth = selectedNodesCount > 1
        ? defaultGraphStrokeWidth / 2
        : defaultGraphStrokeWidth;
    var lineElements = useMemo(function () {
        if (pumpsAndCupsNodeTree === undefined || selectedNodes === undefined)
            return null;
        var lines = [];
        pumpsAndCupsNodeTree.forEach(function (vacuumPump) {
            var connectedSuctionCups = vacuumPump.connectedSuctionCups;
            connectedSuctionCups.forEach(function (suctionCup) {
                var suctionCupId = suctionCup.yValueKey, suctionCupColor = suctionCup.color, suctionCupName = suctionCup.name, alphabeticalId = suctionCup.alphabeticalId;
                var suctionCupLabel = getSuctionCupLabel({
                    alphabeticalId: alphabeticalId,
                    name: suctionCupName,
                });
                var verticalForceDataKey = liftingForcesYAxisVerticalKey + "-" + suctionCup.nodeId;
                var horizontalForceDataKey = liftingForcesYAxisHorizontalKey + "-" + suctionCup.nodeId;
                var isSelected = selectedNodes[suctionCupId];
                if (isSelected) {
                    lines.push(_jsx(Line, { type: "linear", dataKey: verticalForceDataKey, stroke: suctionCupColor, strokeWidth: strokeWidth, dot: false, name: suctionCupLabel }, verticalForceDataKey));
                    lines.push(_jsx(Line, { type: "linear", dataKey: horizontalForceDataKey, stroke: suctionCupColor, strokeWidth: strokeWidth, dot: false, strokeDasharray: "6 4", name: suctionCupLabel }, horizontalForceDataKey));
                }
            });
        });
        return lines;
    }, [getSuctionCupLabel, pumpsAndCupsNodeTree, selectedNodes, strokeWidth]);
    // One total per lifted box, in the colours of the box badges on the canvas
    // (green holds, red falls short), so the graph and the 3D view agree.
    var liftedBoxes = liftingForcesGraphData === null || liftingForcesGraphData === void 0 ? void 0 : liftingForcesGraphData.boxes;
    var boxLineElements = useMemo(function () {
        if (!(liftedBoxes === null || liftedBoxes === void 0 ? void 0 : liftedBoxes.length))
            return null;
        return liftedBoxes.flatMap(function (_a) {
            var box = _a.box, boxNumber = _a.boxNumber;
            var color = box.partSecured
                ? theme.colors.palette.green[500]
                : theme.colors.palette.red[500];
            var name = t("graph_panel.box_series_name", { boxNumber: boxNumber });
            var verticalDataKey = boxLiftingForceDataKey(liftingForcesYAxisVerticalKey, box.boxId);
            var horizontalDataKey = boxLiftingForceDataKey(liftingForcesYAxisHorizontalKey, box.boxId);
            return [
                _jsx(Line, { type: "linear", dataKey: verticalDataKey, stroke: color, strokeWidth: defaultGraphStrokeWidth, dot: false, name: name }, verticalDataKey),
                _jsx(Line, { type: "linear", dataKey: horizontalDataKey, stroke: color, strokeWidth: defaultGraphStrokeWidth, dot: false, strokeDasharray: "6 4", name: name }, horizontalDataKey),
            ];
        });
    }, [liftedBoxes, t, theme]);
    var zoomTicksX = useMemo(function () {
        if (!zoomedTimeDomain)
            return;
        var min = zoomedTimeDomain[0];
        var max = zoomedTimeDomain[1];
        var ticksX = [];
        var addIndexIfInRange = function (index) {
            var isInRange = index < max && index > min;
            if (!isInRange)
                return;
            ticksX.push(index);
        };
        addIndexIfInRange(timeData[psIndex]);
        addIndexIfInRange(timeData[cupReleaseIndex]);
        if (showEnergySave) {
            addIndexIfInRange(timeData[esIndex]);
            ticksX.sort();
        }
        ticksX.push(max);
        return ticksX;
    }, [
        cupReleaseIndex,
        esIndex,
        psIndex,
        showEnergySave,
        timeData,
        zoomedTimeDomain,
    ]);
    var zoomTicksY = useMemo(function () {
        if (!zoomedLiftForceDomain)
            return;
        var min = zoomedLiftForceDomain[0];
        var max = zoomedLiftForceDomain[1];
        var ticks = [zoomedLiftForceDomain[0], zoomedLiftForceDomain[1]];
        if (canFitTickForZero({ min: min, max: max }, ticks)) {
            return __spreadArray([0], __read(ticks), false);
        }
        var ticksWithClashingTickRemoved = __spreadArray([], __read(ticks), false);
        var closestIndex = ticks.reduce(function (minIdx, val, idx, array) {
            return Math.abs(val) < Math.abs(array[minIdx]) ? idx : minIdx;
        }, 0);
        // Replace the closest tick with zero
        ticksWithClashingTickRemoved[closestIndex] = 0;
        return ticksWithClashingTickRemoved;
    }, [zoomedLiftForceDomain]);
    var liftingForcesData = liftingForcesGraphData === null || liftingForcesGraphData === void 0 ? void 0 : liftingForcesGraphData.data;
    var filteredLiftingForcesData = useMemo(function () {
        if (!zoomedTimeDomain || !liftingForcesData)
            return liftingForcesData;
        return filterDataToDomain(zoomedTimeDomain, liftingForcesData);
    }, [zoomedTimeDomain, liftingForcesData]);
    if (!liftingForcesGraphData)
        return null;
    var liftingForcesDomain = liftingForcesGraphData.domain, requiredForces = liftingForcesGraphData.requiredForces;
    // this reassignment is a hack to make the graph ticks start from zero,
    // ignoring the domain min value that can be slightly less than zero. we do it
    // here for display purposes only, the actual domain from the selector is not
    // changed.
    var liftingForcesDomainThatStartsFromZero = [0, liftingForcesDomain[1]];
    var yAxisTicks = zoomTicksY !== null && zoomTicksY !== void 0 ? zoomTicksY : liftingForcesDomainThatStartsFromZero;
    var xDomain = zoomedTimeDomain !== null && zoomedTimeDomain !== void 0 ? zoomedTimeDomain : xAxisDomain;
    var yDomain = zoomedLiftForceDomain !== null && zoomedLiftForceDomain !== void 0 ? zoomedLiftForceDomain : liftingForcesDomainThatStartsFromZero;
    var yAxisUnitconversion = conversions[liftingForcesYAxisUnit];
    var yAxisUnit = unitSystem === UnitSystem.METRIC
        ? liftingForcesYAxisUnit
        : yAxisUnitconversion.unit;
    var xAxisTicks = zoomTicksX
        ? zoomTicksX
        : [timeData[psIndex], timeData[cupReleaseIndex]];
    if (showEnergySave && !zoomTicksX) {
        xAxisTicks.push(timeData[esIndex]);
        xAxisTicks.sort();
    }
    // The two requirements often agree (same weight, same safety factors), in
    // which case the lines overlap and get one shared label; otherwise the
    // vertical label sits above its line and the horizontal below, so nearby
    // values never pile their labels on top of each other.
    var requiredLinesCoincide = requiredForces !== undefined &&
        Math.abs(requiredForces.vertical - requiredForces.horizontal) < 0.5;
    var zoomedRangeIncludeZero = zoomedLiftForceDomain &&
        zoomedLiftForceDomain[0] <= 0 &&
        zoomedLiftForceDomain[1] >= 0;
    return (_jsxs(LineChart, { width: graphWidth, height: liftingForcesGraphHeight, data: filteredLiftingForcesData, margin: __assign(__assign({}, graphMargins), { bottom: 0 }), children: [_jsx(CartesianGrid, { vertical: false, horizontal: false }), _jsx(XAxis, __assign({}, defaultXAxisProps, { ticks: xAxisTicks, domain: xDomain, tick: _jsx(CustomXAxisTick, {}), interval: 0, height: 35 })), _jsx(YAxis, __assign({}, defaultYAxisProps, { interval: 0, unit: yAxisUnit, tick: _jsx(YAxisTick, {}), ticks: yAxisTicks, tickLine: false, padding: yAxisPadding, domain: yDomain })), _jsx(Tooltip, { allowEscapeViewBox: { x: true, y: true }, position: liftingForceTooltipPosition, wrapperStyle: { zIndex: 100 }, content: _jsx(CustomLiftingForcesGraphTooltip, { xLabel: t("graph_panel.pump_x_label_time"), yLabel: t("graph_panel.lifting_forces_tooltip_y_label"), yUnit: liftingForcesYAxisUnit }) }), lineElements, boxLineElements, requiredForces && (_jsxs(_Fragment, { children: [_jsx(ReferenceLine, { y: requiredForces.vertical, stroke: liftingForcesColor, strokeWidth: 2, label: {
                            value: requiredLinesCoincide
                                ? t("graph_panel.required_force_both")
                                : t("graph_panel.required_force_vertical"),
                            position: "insideTopRight",
                            fill: liftingForcesColor,
                            fontSize: 10,
                        } }), _jsx(ReferenceLine, { y: requiredForces.horizontal, stroke: liftingForcesColor, strokeWidth: 2, strokeDasharray: "6 4", label: requiredLinesCoincide
                            ? undefined
                            : {
                                value: t("graph_panel.required_force_horizontal"),
                                position: "insideBottomRight",
                                fill: liftingForcesColor,
                                fontSize: 10,
                            } })] })), _jsx(VerticalReferenceLines, { withLabel: true }), zoomedRangeIncludeZero && _jsx(ReferenceLine, { y: 0 }), _jsx(ReferenceLine, { y: yAxisTicks[0] }), _jsx(ReferenceLine, { y: yAxisTicks[1] }), _jsx(Legend, { align: "left", wrapperStyle: customLegendWrapperStyle, content: function () { return _jsx(LiftingForcesLegend, {}); } })] }));
};
